import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { styled } from '@mui/material/styles';
import html2canvas from 'html2canvas';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ExperimentResult } from '../../interfaces/Experiments';
import { parseExperimentResults } from '../../interfaces/Experiments';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  border: '2px dashed #ccc',
  backgroundColor: theme.palette.background.paper,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

interface SummaryMetric {
  label: string;
  metric: string;
  baseline?: number;
  treatment?: number;
  percentChange?: number;
  pValue?: number;
  isSignificant?: boolean;
}

interface UploadedFile {
  id: string;
  file: File;
  data: ExperimentResult[];
  name: string;
  uploadTime: Date;
}

function forecast(
  x: number,
  knownYs: [number, number],
  knownXs: [number, number],
) {
  if (knownYs.length !== knownXs.length || knownYs.length < 2) {
    throw new Error(
      'Input arrays must have the same length and contain at least 2 points.',
    );
  }

  const n = knownYs.length;
  const sumX = knownXs.reduce((a, b) => a + b, 0);
  const sumY = knownYs.reduce((a, b) => a + b, 0);
  const sumXY = knownXs.reduce((sum, val, i) => sum + val * knownYs[i]!, 0);
  const sumX2 = knownXs.reduce((sum, val) => sum + val * val, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  return slope * x + intercept;
}

// Helper function to extract the number from a metric name
function extractNumberFromMetric(metricName: string): number {
  // Handle patterns like:
  // - Perc_Orders_Less_67p5 -> 67.5
  // - Perc_Orders_Less_75_3PD -> 75
  // - Perc_Orders_Less_100_1PD -> 100

  const parts = metricName.split('_');
  const lastPart = parts[parts.length - 1];

  // If the last part is a number (possibly with 'p' for decimal), use it
  if (/^\d+(?:p\d+)?$/.test(lastPart)) {
    return +lastPart.replace('p', '.');
  }

  // If the last part is 1PD or 3PD, look at the second-to-last part
  if (lastPart === '1PD' || lastPart === '3PD') {
    const secondLastPart = parts[parts.length - 2];
    if (/^\d+(?:p\d+)?$/.test(secondLastPart)) {
      return +secondLastPart.replace('p', '.');
    }
  }

  // Fallback: try to find any number in the metric name
  const numberMatch = metricName.match(/(\d+(?:p\d+)?)/);
  if (numberMatch) {
    return +numberMatch[1].replace('p', '.');
  }

  return 0; // Fallback
}

// Helper function to find percentile value through linear interpolation
function findPercentileValue(
  percentile: number,
  percentileMetrics: ExperimentResult[],
): SummaryMetric {
  const controlMetricsAsc: ExperimentResult[] = [...percentileMetrics]
    .filter((metric) => metric.meanBaseline)
    .sort((a, b) => a.meanBaseline! - b.meanBaseline!);

  // If no valid metrics found, return empty result
  if (controlMetricsAsc.length === 0) {
    return {
      label: `P${percentile} Delivery Time`,
      metric: '',
      baseline: undefined,
      treatment: undefined,
      percentChange: undefined,
      pValue: undefined,
      isSignificant: undefined,
    };
  }

  const treatmentMetricsAsc: ExperimentResult[] = [...controlMetricsAsc]
    .map((metric) => ({
      ...metric,
      meanBaseline: metric.meanBaseline! * (1 + (metric.percChange || 0)),
    }))
    .sort((a, b) => a.meanBaseline! - b.meanBaseline!);

  // largest baseline < percentile/100
  const lowerMetric = controlMetricsAsc
    .filter((m: ExperimentResult) => m.meanBaseline! < percentile / 100)
    .at(-1);
  // smallest baseline >= percentile/100
  const higherMetric = controlMetricsAsc
    .filter((m: ExperimentResult) => m.meanBaseline! >= percentile / 100)
    .at(0);

  // Check if we have valid metrics
  if (!lowerMetric || !higherMetric) {
    return {
      label: `P${percentile} Delivery Time`,
      metric: '',
      baseline: undefined,
      treatment: undefined,
      percentChange: undefined,
      pValue: undefined,
      isSignificant: undefined,
    };
  }

  const controlYVals: [number, number] = [
    extractNumberFromMetric(lowerMetric.metric),
    extractNumberFromMetric(higherMetric.metric),
  ];
  const controlXVals: [number, number] = [
    lowerMetric.meanBaseline!,
    higherMetric.meanBaseline!,
  ];
  const controlMetric = forecast(percentile / 100, controlYVals, controlXVals);

  const treatmentLowerMetric = treatmentMetricsAsc
    .filter((m: ExperimentResult) => m.meanBaseline! < percentile / 100)
    .at(-1);
  const treatmentHigherMetric = treatmentMetricsAsc
    .filter((m: ExperimentResult) => m.meanBaseline! >= percentile / 100)
    .at(0);

  // Check if we have valid treatment metrics
  if (!treatmentLowerMetric || !treatmentHigherMetric) {
    return {
      label: `P${percentile} Delivery Time`,
      metric: '',
      baseline: controlMetric,
      treatment: undefined,
      percentChange: undefined,
      pValue: lowerMetric.pVal,
      isSignificant: lowerMetric.pVal ? lowerMetric.pVal < 0.05 : undefined,
    };
  }

  const treatmentYVals: [number, number] = [
    extractNumberFromMetric(treatmentLowerMetric.metric),
    extractNumberFromMetric(treatmentHigherMetric.metric),
  ];
  const treatmentXVals: [number, number] = [
    treatmentLowerMetric.meanBaseline!,
    treatmentHigherMetric.meanBaseline!,
  ];
  const treatmentMetric = forecast(
    percentile / 100,
    treatmentYVals,
    treatmentXVals,
  );

  // find which baseline between lowerIndex and upperIndex is closest to the percentile/100
  const closestMetric =
    percentile / 100 - controlXVals[0] < controlXVals[1] - percentile / 100
      ? lowerMetric
      : higherMetric;

  return {
    label: `P${percentile} Delivery Time`,
    metric: closestMetric.metric,
    baseline: controlMetric,
    treatment: treatmentMetric,
    // calculated as (treatment - baseline) / baseline
    percentChange:
      controlMetric && treatmentMetric
        ? (treatmentMetric - controlMetric) / controlMetric
        : undefined,
    pValue: closestMetric.pVal,
    isSignificant: closestMetric.pVal ? closestMetric.pVal < 0.05 : undefined,
  };
}

// Calculate the delta between baseline and treatment values
const calculateDelta = (
  baseline?: number,
  treatment?: number,
): number | undefined => {
  if (baseline === undefined || treatment === undefined) return undefined;
  return treatment - baseline;
};

export default function ExperimentAnalysis() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [currentFileId, setCurrentFileId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const significanceThreshold = 0.05;
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [fileMenuAnchor, setFileMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [filterMetric, setFilterMetric] = useState<string>('');
  const [filterDataSubset, setFilterDataSubset] = useState<string>('');
  const [showSignificantOnly, setShowSignificantOnly] =
    useState<boolean>(false);
  const [generalSummaryDataSubset, setGeneralSummaryDataSubset] =
    useState<string>('All');
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const exportButtonRef = useRef<HTMLButtonElement>(null);

  // Focus export button when dialog opens
  useEffect(() => {
    if (exportDialogOpen) {
      // Small delay to ensure the dialog is fully rendered
      setTimeout(() => {
        if (exportButtonRef.current) {
          exportButtonRef.current.focus();
        }
      }, 100);
    }
  }, [exportDialogOpen]);

  // Get current file data
  const currentFile = useMemo(() => {
    return uploadedFiles.find((file) => file.id === currentFileId) || null;
  }, [uploadedFiles, currentFileId]);

  const csvData = currentFile?.data || null;

  const analysisData = useMemo(() => {
    if (!csvData) return null;

    const dataSubsets = Array.from(
      new Set(csvData.map((item) => item.dataSubset)),
    );
    const metrics = Array.from(new Set(csvData.map((item) => item.metric)));

    return {
      dataSubsets,
      metrics,
    };
  }, [csvData, significanceThreshold]);

  const derivedMetrics = useMemo<SummaryMetric[]>(() => {
    if (!csvData) return [];

    // Filter for selected data subset and percentile metrics
    const dataSubsetResults = csvData.filter(
      (result) =>
        result.dataSubset === generalSummaryDataSubset &&
        /^Perc_Orders_Less_\d+(?:p\d+)?$/.test(result.metric),
    );

    if (dataSubsetResults.length === 0) return [];

    const p50 = findPercentileValue(50, dataSubsetResults);
    const p70 = findPercentileValue(70, dataSubsetResults);
    // Calculate P90 (find where percentile = 90%)
    const p90 = findPercentileValue(90, dataSubsetResults);

    // Calculate P97 (find where percentile = 97%)
    const p97 = findPercentileValue(97, dataSubsetResults);

    // Calculate % of orders > 65 minutes (100% - Perc_Orders_Less_65)
    const perc65MinResult = dataSubsetResults.find(
      (r) => r.metric === 'Perc_Orders_Less_65',
    );
    const ordersOver65MinBaseline = perc65MinResult?.meanBaseline
      ? 100 - perc65MinResult.meanBaseline * 100
      : undefined;
    const ordersOver65MinTreatment =
      perc65MinResult?.meanBaseline && perc65MinResult?.percChange
        ? 100 -
          perc65MinResult.meanBaseline * (1 + perc65MinResult.percChange) * 100
        : undefined;
    const ordersOver65MinPercentChange =
      ordersOver65MinBaseline && ordersOver65MinTreatment
        ? (ordersOver65MinTreatment - ordersOver65MinBaseline) /
          ordersOver65MinBaseline
        : undefined;

    return [
      p50,
      p70,
      p90,
      p97,
      {
        label: '% Orders > 65 minutes',
        metric: '% Orders > 65 minutes',
        baseline: ordersOver65MinBaseline,
        treatment: ordersOver65MinTreatment,
        percentChange: ordersOver65MinPercentChange,
        pValue: perc65MinResult?.pVal,
        isSignificant: perc65MinResult?.pVal
          ? perc65MinResult.pVal < significanceThreshold
          : undefined,
      },
    ];
  }, [csvData, significanceThreshold, generalSummaryDataSubset]);

  const summaryConfig = useMemo(() => {
    return [
      {
        title: 'General Summary',
        categories: [
          {
            name: 'Delivery Times',
            metrics: [], // Will be populated with derived metrics
          },
          {
            name: 'DCPO',
            metrics: ['DCPO_Excl_Tips_ALL', 'Cost_3PD_Per_Order_ALL'],
          },
          {
            name: 'Driver Supply',
            metrics: [
              'Perc_Orders_By_1PD',
              'Perc_Orders_By_3PD',
              'TOTAL_COMPLETED_ORDERS',
              'NUM_COMPLETED_ORDERS_1PD',
              'NUM_COMPLETED_ORDERS_3PD',
            ],
          },
          {
            name: 'Driver Pay & Engagement',
            metrics: [
              'Driver_Pay_Per_Hr_Excl_Tips',
              'Driver_Pay_Per_Hr_Incl_Tips',
              'Driver_Pay_Per_Hr_Only_Tips',
              'Driver_Pay_Per_Active_Hr_Excl_Tips',
              'Driver_Pay_Per_Active_Hr_Incl_Tips',
              'ODH',
              'ODH_Active_Driver_Hours',
              'Driver_Util',
              // 'Perc_Driver_Hrs_Queued',
              // 'Perc_Driver_Hrs_Delivering',
              // 'Perc_Driver_Hrs_Returning',
              // 'Perc_Driver_Hrs_Unknown',
              // 'COUNT_DRIVER_HRS',
              // 'COUNT_SCHEDULED_HRS',
              // 'COUNT_UNSCHEDULED_HRS',
              // 'COUNT_DRIVER_QUEUED_HRS',
              // 'COUNT_DRIVER_DELIVERING_HRS',
              // 'COUNT_DRIVER_RETURNING_HRS',
              // 'COUNT_DRIVER_UNKNOWN_HRS',
            ],
          },
          {
            name: 'Offer Summary',
            metrics: [
              'Perc_Trips_Batched',
              'Orders_Per_Trip',
              'Orders_Per_Trip_DPP',
              'Perc_Orders_Unbatched_DPP',
              'Perc_Orders_Batched_DPP',
              'Perc_Trips_Unbatched_DPP',
              'Perc_Trips_Batched_DPP',
              'Perc_Trips_Batched_2_DPP',
              'Perc_Trips_Batched_3_DPP',
              'Perc_Trips_Batched_4Plus_DPP',
              'Perc_Trips_Upfront_Dispatch_DPP',
              'Perc_Upfront_Dispatch_Trips_Expanded_DPP',
              'Perc_Trip_Candidates_Accepted_DPP',
              'Perc_Trip_Candidates_Expired_DPP',
              'Perc_Trip_Candidates_Rejected_DPP',
              'Perc_Offered_Upfront_Trips_DPP',
              'Perc_Offered_Upfront_Trips_Expanded_DPP',
              'Perc_Expanded_Upfront_Trips_Accepted_DPP',
              'Perc_Candidate_Orders_Unassigned_DPP',
              'Perc_Candidate_Orders_Cancelled_Post_DPP',
              'Perc_Candidate_Orders_Cancelled_Pre_DPP',
              'Perc_Proactive_Trips_DPP',
              'Num_Proactive_Trips_DPP',
              'Perc_Deferred_Orders',
              'Perc_Deferred_Batched_Orders',
              'Perc_Deferred_Unbatched_Orders',
              'Num_Deferred_Orders',
              'Num_Deferred_Batched_Orders',
              'Num_Deferred_Unbatched_Orders',
              'Num_Productive_Unbatched_Deferrals',
              'Num_Unproductive_Unbatched_Deferrals',
            ],
          },
        ],
      },
    ];
  }, []);

  const summaryData = useMemo(() => {
    if (!csvData) return [];

    const allDataSubsetResults = csvData.filter(
      (result) => result.dataSubset === 'All',
    );
    const generalSummaryDataSubsetResults = csvData.filter(
      (result) => result.dataSubset === generalSummaryDataSubset,
    );
    if (allDataSubsetResults.length === 0) return [];

    return summaryConfig.map((section, _index) => {
      // Special handling for "General Summary" section with categories
      if (section.title === 'General Summary' && section.categories) {
        const categories = section.categories.map((category) => {
          let metrics;

          // Special handling for "Delivery Times" category - use derived metrics
          if (category.name === 'Delivery Times') {
            metrics = derivedMetrics;
          } else {
            // Handle other categories with their configured metrics
            metrics = category.metrics.map((metricName) => {
              const result = generalSummaryDataSubsetResults.find(
                (r) => r.metric === metricName,
              );

              return {
                label: metricName,
                metric: metricName,
                baseline: result?.meanBaseline,
                treatment: result?.meanBaseline
                  ? result.meanBaseline * (1 + result.percChange)
                  : undefined,
                percentChange: result?.percChange,
                pValue: result?.pVal,
                isSignificant: result?.pVal
                  ? result.pVal < significanceThreshold
                  : undefined,
              };
            });
          }

          return {
            name: category.name,
            metrics,
          };
        });

        return {
          title: section.title,
          categories,
        };
      }

      // Since we only have the General Summary section now, return empty for any other sections
      return {
        title: section.title,
        metrics: [],
      };
    });
  }, [
    csvData,
    summaryConfig,
    significanceThreshold,
    derivedMetrics,
    generalSummaryDataSubset,
  ]);

  // Extract file processing logic to a separate function
  const processFile = async (f: File) => {
    setIsUploading(true);

    try {
      const text = await f.text();
      const results = parseExperimentResults(text);

      const newFile: UploadedFile = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file: f,
        data: results,
        name: f.name,
        uploadTime: new Date(),
      };

      setUploadedFiles((prev) => [...prev, newFile]);
      setCurrentFileId(newFile.id);
      setSnackbarMessage(
        `File uploaded successfully. Parsed ${results.length} experiment results.`,
      );
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error processing CSV:', error);
      setSnackbarMessage('Error processing the CSV file');
      setSnackbarOpen(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      console.log(
        'Selected files:',
        files.map((f) => ({ name: f.name, type: f.type })),
      );

      for (const selectedFile of files) {
        // More flexible validation - check if it's a CSV file
        const isCSV =
          selectedFile.name.toLowerCase().endsWith('.csv') ||
          selectedFile.type === 'text/csv' ||
          selectedFile.type === 'application/csv' ||
          selectedFile.type === '';

        if (!isCSV) {
          setSnackbarMessage(
            `Please upload CSV files only. File "${selectedFile.name}" is not a CSV file.`,
          );
          setSnackbarOpen(true);
          continue;
        }

        // Process each file
        processFile(selectedFile);
      }
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (event.dataTransfer.files) {
      const files = Array.from(event.dataTransfer.files);
      console.log(
        'Dropped files:',
        files.map((f) => ({ name: f.name, type: f.type })),
      );

      for (const droppedFile of files) {
        // More flexible validation - check if it's a CSV file
        const isCSV =
          droppedFile.name.toLowerCase().endsWith('.csv') ||
          droppedFile.type === 'text/csv' ||
          droppedFile.type === 'application/csv' ||
          droppedFile.type === '';

        if (!isCSV) {
          setSnackbarMessage(
            `Please upload CSV files only. File "${droppedFile.name}" is not a CSV file.`,
          );
          setSnackbarOpen(true);
          continue;
        }

        // Process each file
        processFile(droppedFile);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles((prev) => {
      const newFiles = prev.filter((f) => f.id !== fileId);
      if (currentFileId === fileId) {
        setCurrentFileId(newFiles.length > 0 ? newFiles[0]?.id || null : null);
      }
      return newFiles;
    });
  };

  const formatPercentChange = (value: number): string => {
    const formatted = (value * 100)?.toFixed(2);
    return value >= 0 ? `+${formatted}%` : `${formatted}%`;
  };

  const handleExportGeneralSummary = () => {
    // Initialize with all categories selected by default
    const allCategories =
      summaryData[0]?.categories?.map((cat) => cat.name) || [];
    setSelectedCategories(allCategories);
    setExportDialogOpen(true);
  };

  const exportGeneralSummaryWithSelection = () => {
    if (selectedCategories.length === 0) {
      setSnackbarMessage('Please select at least one category to export');
      setSnackbarOpen(true);
      return;
    }

    setIsExporting(true);

    // Small delay to ensure loading state is visible
    setTimeout(() => {
      // Create a temporary container with only selected categories
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.padding = '20px';
      tempContainer.style.fontFamily = 'Roboto, sans-serif';

      // Add title
      const title = document.createElement('h4');
      title.textContent = 'General Summary';
      title.style.margin = '0 0 20px 0';
      title.style.color = '#1976d2';
      title.style.fontSize = '24px';
      tempContainer.appendChild(title);

      // Add file information
      const fileInfo = document.createElement('div');
      fileInfo.style.margin = '0 0 30px 0';
      fileInfo.style.padding = '20px';
      fileInfo.style.backgroundColor = '#f8f9fa';
      fileInfo.style.borderRadius = '8px';
      fileInfo.style.border = '1px solid #dee2e6';
      fileInfo.style.fontSize = '14px';
      fileInfo.style.color = '#495057';
      fileInfo.style.lineHeight = '1.5';

      const fileName = currentFile?.name || 'Unknown File';
      const dataSubset = generalSummaryDataSubset || 'All';

      fileInfo.innerHTML = `
        <strong>File:</strong> ${fileName}<br>
        <strong>Data Subset:</strong> ${dataSubset}<br>
      `;

      tempContainer.appendChild(fileInfo);

      // Add selected categories
      selectedCategories.forEach((categoryName) => {
        const categoryElement = document.querySelector(
          `[data-category-title="${categoryName}"]`,
        );
        if (categoryElement) {
          const clonedElement = categoryElement.cloneNode(true) as HTMLElement;
          // Remove the export button if it exists
          const exportButton = clonedElement.querySelector('[title*="Export"]');
          if (exportButton) {
            exportButton.remove();
          }
          tempContainer.appendChild(clonedElement);
        }
      });

      document.body.appendChild(tempContainer);

      html2canvas(tempContainer, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
      })
        .then((canvas: HTMLCanvasElement) => {
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              const categorySuffix =
                selectedCategories.length === summaryData[0]?.categories?.length
                  ? 'General_Summary'
                  : selectedCategories.join('_');
              const exportDataSubset = generalSummaryDataSubset || 'All';
              const sanitizedDataSubset = exportDataSubset.replace(
                /[^a-zA-Z0-9]/g,
                '_',
              );
              a.download = `${currentFile?.name?.replace(
                '.csv',
                '',
              )}_${sanitizedDataSubset}_${categorySuffix}.png`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);

              setSnackbarMessage(
                `Successfully exported ${selectedCategories.length} category${
                  selectedCategories.length === 1 ? '' : 'ies'
                } as image`,
              );
              setSnackbarOpen(true);
            }
          });
          document.body.removeChild(tempContainer);
          setIsExporting(false);
          setExportDialogOpen(false);
        })
        .catch((error: any) => {
          console.error('Export failed:', error);
          setSnackbarMessage('Export failed. Please try again.');
          setSnackbarOpen(true);
          document.body.removeChild(tempContainer);
          setIsExporting(false);
          setExportDialogOpen(false);
        });
    }, 10); // Small delay to ensure loading state is visible
  };

  const getPValueColor = (pVal: number): string => {
    if (pVal < 0.01) return theme.palette.success.main;
    if (pVal < 0.05) return theme.palette.success.light;
    if (pVal < 0.1) return theme.palette.warning.main;
    return theme.palette.text.secondary;
  };

  const filteredMetrics = useMemo(() => {
    if (!csvData) return [];

    return csvData.filter((metric) => {
      const matchesMetric = filterMetric
        ? metric.metric.includes(filterMetric)
        : true;
      const matchesDataSubset = filterDataSubset
        ? metric.dataSubset.includes(filterDataSubset)
        : true;
      const matchesSignificance = showSignificantOnly
        ? metric.pVal && metric.pVal < significanceThreshold
        : true;

      return matchesMetric && matchesDataSubset && matchesSignificance;
    });
  }, [
    csvData,
    filterMetric,
    filterDataSubset,
    showSignificantOnly,
    significanceThreshold,
  ]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Paper sx={{ padding: 3, margin: 2, maxWidth: '1200px', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography gutterBottom variant='h4'>
            Experiment Analysis
          </Typography>
          <Button
            onClick={() => navigate('/experiments')}
            startIcon={<ArrowBackIcon />}
            variant='outlined'
          >
            Back to Experiments
          </Button>
        </Box>

        {uploadedFiles.length === 0 ? (
          <>
            <Typography component='p' variant='body1'>
              Upload CSV files containing experiment data for analysis. You can
              upload multiple files and switch between them.
            </Typography>

            <UploadBox
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDrop={handleFileDrop}
            >
              <CloudUploadIcon
                color='primary'
                fontSize='large'
                sx={{ mb: 2 }}
              />
              <Typography gutterBottom variant='h6'>
                Drag and drop CSV files here
              </Typography>
              <Typography gutterBottom color='textSecondary' variant='body2'>
                or
              </Typography>
              <Button
                component='label'
                startIcon={<CloudUploadIcon />}
                variant='contained'
              >
                Select CSV Files
                <VisuallyHiddenInput
                  key={`file-input-${uploadedFiles.length}`}
                  multiple
                  accept='.csv,text/csv,application/csv'
                  onChange={handleFileChange}
                  type='file'
                />
              </Button>
              {isUploading && (
                <Box mt={2} textAlign='center'>
                  <CircularProgress size={24} />
                  <Typography sx={{ mt: 1 }} variant='body2'>
                    Processing files...
                  </Typography>
                </Box>
              )}
            </UploadBox>
          </>
        ) : (
          csvData &&
          analysisData && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant='h5'>
                  Analysis Results ({csvData?.length || 0} data points)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {uploadedFiles.length > 1 && (
                    <Autocomplete
                      getOptionLabel={(option) => option.name}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setCurrentFileId(newValue.id);
                        }
                      }}
                      options={uploadedFiles}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Select File'
                          size='small'
                          sx={{ minWidth: 500 }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component='li' {...props}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <CheckCircleIcon
                              sx={{
                                mr: 1,
                                color:
                                  currentFileId === option.id
                                    ? 'primary.main'
                                    : 'disabled',
                                fontSize: '1rem',
                              }}
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography noWrap variant='body2'>
                                {option.name}
                              </Typography>
                              <Typography
                                color='text.secondary'
                                variant='caption'
                              >
                                {option.data.length} data points •{' '}
                                {option.uploadTime.toLocaleTimeString()}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                      value={currentFile}
                    />
                  )}
                  {uploadedFiles.length > 1 && (
                    <IconButton
                      onClick={(event) =>
                        setFileMenuAnchor(event.currentTarget)
                      }
                      size='small'
                      sx={{ ml: 1 }}
                    >
                      ⋮
                    </IconButton>
                  )}
                  <Button
                    onClick={() => {
                      setUploadedFiles([]);
                      setCurrentFileId(null);
                    }}
                    variant='outlined'
                  >
                    Upload New Files
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  background:
                    theme.palette.mode === 'dark'
                      ? 'linear-gradient(45deg, rgba(66,66,77,0.9) 0%, rgba(30,30,40,0.9) 100%)'
                      : 'linear-gradient(45deg, rgba(240,249,255,1) 0%, rgba(214,240,253,1) 100%)',
                  boxShadow: '0 3px 5px rgba(0,0,0,0.08)',
                  border: `1px solid ${theme.palette.divider}`,
                  animation: 'fadeIn 0.5s ease-in-out',
                  '@keyframes fadeIn': {
                    '0%': { opacity: 0, transform: 'translateY(-10px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                  },
                  position: 'relative',
                }}
              >
                {uploadedFiles.length > 1 && (
                  <Chip
                    color='primary'
                    label={`${uploadedFiles.length} files`}
                    size='small'
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: 16,
                      fontSize: '0.75rem',
                    }}
                  />
                )}
                {currentFile && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          p: 1,
                          borderRadius: '50%',
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transform: 'scale(1.2)',
                        }}
                      >
                        <CheckCircleIcon />
                      </Box>
                      <Box>
                        <Typography color='primary' variant='overline'>
                          Currently Analyzing
                        </Typography>
                        <Typography
                          fontWeight='bold'
                          sx={{
                            textShadow: '0px 1px 1px rgba(0,0,0,0.1)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          variant='h5'
                        >
                          {currentFile.name}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      color='primary'
                      label={`${csvData?.length || 0} data points`}
                      sx={{ fontWeight: 'bold' }}
                      variant='filled'
                    />
                  </>
                )}
              </Box>

              {summaryData.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography gutterBottom variant='h5'>
                      Key Metrics Summary - "All" Data Subset
                    </Typography>
                    {currentFile && (
                      <Chip
                        color='primary'
                        label={currentFile.name}
                        size='small'
                        variant='outlined'
                      />
                    )}
                  </Box>

                  <Accordion
                    defaultExpanded
                    data-section-title='General Summary'
                  >
                    <AccordionSummary
                      aria-controls='panel0-content'
                      expandIcon={<ExpandMoreIcon />}
                      id='panel0-header'
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          alignItems: 'center',
                        }}
                      >
                        <Typography fontWeight='bold' variant='subtitle1'>
                          General Summary
                        </Typography>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          {currentFile && (
                            <Typography
                              color='text.secondary'
                              sx={{ ml: 2 }}
                              variant='body2'
                            >
                              File: {currentFile?.name}
                            </Typography>
                          )}
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportGeneralSummary();
                            }}
                            size='small'
                            title='Export as image'
                          >
                            📷
                          </IconButton>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ mb: 2 }}>
                        <Autocomplete
                          onChange={(event, newValue) =>
                            setGeneralSummaryDataSubset(newValue || 'All')
                          }
                          options={[
                            'All',
                            'FAM20 Orders',
                            'Priority Orders',
                            'Fresh Orders',
                            'Newbie Orders',
                            'Regular Orders',
                            'MFC Volume Tier 1',
                            'MFC Volume Tier 2',
                            'MFC Volume Tier 3',
                            'MFC Volume Tier 4',
                          ]}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Data Subset'
                              size='small'
                            />
                          )}
                          size='small'
                          sx={{ width: 250 }}
                          value={generalSummaryDataSubset}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 3,
                        }}
                      >
                        {/* Categorized Metrics */}
                        {summaryData[0]?.categories?.map((category) => (
                          <Box
                            key={`category-${category.name}`}
                            data-category-title={category.name}
                          >
                            <Typography
                              sx={{
                                mb: 2,
                                fontWeight: 'bold',
                                color: theme.palette.primary.main,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                              variant='h6'
                            >
                              {category.name === 'Delivery Times' && '⏱️ '}
                              {category.name === 'DCPO' && '💰 '}
                              {category.name === 'Driver Supply' && '🚗 '}
                              {category.name === 'Driver Pay & Engagement' &&
                                '🧲 '}
                              {category.name === 'Offer Summary' && '📋 '}
                              {category.name}
                            </Typography>
                            <TableContainer
                              component={Paper}
                              variant='outlined'
                            >
                              <Table size='small'>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>
                                      <strong>Metric</strong>
                                    </TableCell>
                                    <TableCell align='right'>
                                      <strong>Baseline</strong>
                                    </TableCell>
                                    <TableCell align='right'>
                                      <strong>Treatment</strong>
                                    </TableCell>
                                    <TableCell align='right'>
                                      <strong>Delta</strong>
                                    </TableCell>
                                    <TableCell align='right'>
                                      <strong>Change</strong>
                                    </TableCell>
                                    <TableCell align='right'>
                                      <strong>p-value</strong>
                                    </TableCell>
                                    <TableCell align='center'>
                                      <strong>Significant</strong>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {category.metrics.map((metric) => (
                                    <TableRow
                                      key={`category-${category.name}-${metric.label}`}
                                      hover
                                      sx={{
                                        backgroundColor:
                                          metric.isSignificant &&
                                          metric.percentChange
                                            ? 'rgba(255, 255, 170, 0.5)'
                                            : 'inherit',
                                      }}
                                    >
                                      <TableCell>{metric.label}</TableCell>
                                      <TableCell align='right'>
                                        {metric.baseline
                                          ? metric.baseline?.toFixed(2)
                                          : '—'}
                                      </TableCell>
                                      <TableCell align='right'>
                                        {metric.treatment
                                          ? metric.treatment?.toFixed(2)
                                          : '—'}
                                      </TableCell>
                                      <TableCell align='right'>
                                        {calculateDelta(
                                          metric.baseline,
                                          metric.treatment,
                                        )?.toFixed(2) || '—'}
                                      </TableCell>
                                      <TableCell
                                        align='right'
                                        sx={{
                                          color: metric.percentChange
                                            ? metric.percentChange > 0
                                              ? 'success.main'
                                              : metric.percentChange < 0
                                              ? 'error.main'
                                              : 'text.primary'
                                            : 'text.secondary',
                                        }}
                                      >
                                        {metric.percentChange ? (
                                          <>
                                            {formatPercentChange(
                                              metric.percentChange,
                                            )}
                                            {metric.percentChange > 0 ? (
                                              <TrendingUpIcon
                                                fontSize='small'
                                                sx={{
                                                  verticalAlign: 'bottom',
                                                  ml: 0.5,
                                                }}
                                              />
                                            ) : metric.percentChange < 0 ? (
                                              <TrendingDownIcon
                                                fontSize='small'
                                                sx={{
                                                  verticalAlign: 'bottom',
                                                  ml: 0.5,
                                                }}
                                              />
                                            ) : null}
                                          </>
                                        ) : (
                                          '—'
                                        )}
                                      </TableCell>
                                      <TableCell
                                        align='right'
                                        sx={{
                                          color: metric.pValue
                                            ? getPValueColor(metric.pValue)
                                            : 'text.secondary',
                                        }}
                                      >
                                        {metric.pValue
                                          ? metric.pValue?.toFixed(4)
                                          : '—'}
                                      </TableCell>
                                      <TableCell align='center'>
                                        {metric.isSignificant ? (
                                          <CheckCircleIcon
                                            color='success'
                                            fontSize='small'
                                          />
                                        ) : (
                                          '—'
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Alert severity='info'>
                      The summary above shows key metrics for the selected data
                      subset. Use the filter above to view metrics for different
                      order types and volume tiers.
                    </Alert>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mt: 4 }}>
                <Typography gutterBottom variant='h5'>
                  All Results
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Autocomplete
                      onChange={(event, newValue) =>
                        setFilterMetric(newValue || '')
                      }
                      options={analysisData?.metrics || []}
                      renderInput={(params) => (
                        <TextField {...params} label='Filter by Metric' />
                      )}
                      size='small'
                      sx={{ width: 300 }}
                      value={filterMetric}
                    />
                    <Autocomplete
                      onChange={(event, newValue) =>
                        setFilterDataSubset(newValue || '')
                      }
                      options={analysisData?.dataSubsets || []}
                      renderInput={(params) => (
                        <TextField {...params} label='Filter by Data Subset' />
                      )}
                      size='small'
                      sx={{ width: 250 }}
                      value={filterDataSubset}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showSignificantOnly}
                          onChange={(e) =>
                            setShowSignificantOnly(e.target.checked)
                          }
                        />
                      }
                      label='Significant Only'
                    />
                  </Box>
                  <Tooltip title='Clear Filters'>
                    <IconButton
                      onClick={() => {
                        setFilterMetric('');
                        setFilterDataSubset('');
                        setShowSignificantOnly(false);
                      }}
                    >
                      <FilterListIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TableContainer component={Paper} variant='outlined'>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Metric</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Data Subset</strong>
                        </TableCell>
                        <TableCell align='right'>
                          <strong>Baseline</strong>
                        </TableCell>
                        <TableCell align='right'>
                          <strong>Treatment</strong>
                        </TableCell>
                        <TableCell align='right'>
                          <strong>Delta</strong>
                        </TableCell>
                        <TableCell align='right'>
                          <strong>Change</strong>
                        </TableCell>
                        <TableCell align='right'>
                          <strong>p-value</strong>
                        </TableCell>
                        <TableCell align='center'>
                          <strong>Significant</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredMetrics.map((metric, idx) => (
                        <TableRow
                          key={`filtered-metric-${metric.metric + idx}`}
                          hover
                          sx={{
                            backgroundColor:
                              metric.pVal && metric.pVal < significanceThreshold
                                ? 'rgba(255, 255, 170, 0.5)'
                                : 'inherit',
                          }}
                        >
                          <TableCell>{metric.metric}</TableCell>
                          <TableCell>{metric.dataSubset}</TableCell>
                          <TableCell align='right'>
                            {metric.meanBaseline?.toFixed(2) || '—'}
                          </TableCell>
                          <TableCell align='right'>
                            {metric.meanBaseline
                              ? (
                                  metric.meanBaseline *
                                  (1 + metric.percChange)
                                ).toFixed(2)
                              : '—'}
                          </TableCell>
                          <TableCell align='right'>
                            {calculateDelta(
                              metric.meanBaseline,
                              metric.meanBaseline
                                ? metric.meanBaseline * (1 + metric.percChange)
                                : undefined,
                            )?.toFixed(2) || '—'}
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{
                              color: metric.percChange
                                ? metric.percChange > 0
                                  ? 'success.main'
                                  : metric.percChange < 0
                                  ? 'error.main'
                                  : 'text.primary'
                                : 'text.secondary',
                            }}
                          >
                            {metric.percChange ? (
                              <>
                                {formatPercentChange(metric.percChange)}
                                {metric.percChange > 0 ? (
                                  <TrendingUpIcon
                                    fontSize='small'
                                    sx={{
                                      verticalAlign: 'bottom',
                                      ml: 0.5,
                                    }}
                                  />
                                ) : metric.percChange < 0 ? (
                                  <TrendingDownIcon
                                    fontSize='small'
                                    sx={{
                                      verticalAlign: 'bottom',
                                      ml: 0.5,
                                    }}
                                  />
                                ) : null}
                              </>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{
                              color: metric.pVal
                                ? getPValueColor(metric.pVal)
                                : 'text.secondary',
                            }}
                          >
                            {(metric.pVal && metric.pVal?.toFixed(4)) || '—'}
                          </TableCell>
                          <TableCell align='center'>
                            {metric.pVal &&
                            metric.pVal < significanceThreshold ? (
                              <CheckCircleIcon
                                color='success'
                                fontSize='small'
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredMetrics.length === 0 && (
                        <TableRow>
                          <TableCell align='center' colSpan={8}>
                            No metrics match the current filters.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )
        )}

        <Snackbar
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          open={snackbarOpen}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarMessage.includes('success') ? 'success' : 'error'}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* File Management Menu */}
        <Menu
          anchorEl={fileMenuAnchor}
          onClose={() => setFileMenuAnchor(null)}
          open={Boolean(fileMenuAnchor)}
        >
          <MenuItem
            onClick={() => {
              setDrawerOpen(true);
              setFileMenuAnchor(null);
            }}
          >
            Manage Files ({uploadedFiles.length})
          </MenuItem>
          <MenuItem
            onClick={() => {
              setUploadedFiles([]);
              setCurrentFileId(null);
              setFileMenuAnchor(null);
            }}
          >
            Clear All Files
          </MenuItem>
        </Menu>

        {/* File Selection Drawer */}
        <Drawer
          anchor='right'
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          sx={{
            '& .MuiDrawer-paper': {
              width: 400,
              padding: 2,
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography gutterBottom variant='h6'>
              Uploaded Files ({uploadedFiles.length})
            </Typography>
            <List>
              {uploadedFiles.map((file) => (
                <ListItem
                  key={file.id}
                  disablePadding
                  sx={{
                    mb: 1,
                    borderRadius: 1,
                    backgroundColor:
                      currentFileId === file.id
                        ? 'primary.light'
                        : 'transparent',
                  }}
                >
                  <ListItemButton
                    onClick={() => {
                      setCurrentFileId(file.id);
                      setDrawerOpen(false);
                    }}
                    selected={currentFileId === file.id}
                  >
                    <ListItemIcon>
                      <CheckCircleIcon
                        color={
                          currentFileId === file.id ? 'primary' : 'disabled'
                        }
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={`${
                        file.data.length
                      } data points • ${file.uploadTime.toLocaleTimeString()}`}
                    />
                    <IconButton
                      color='error'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(file.id);
                      }}
                      size='small'
                    >
                      ×
                    </IconButton>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {uploadedFiles.length === 0 && (
              <Typography align='center' color='text.secondary'>
                No files uploaded yet
              </Typography>
            )}
          </Box>
        </Drawer>

        {/* Export Categories Dialog */}
        <Dialog
          fullWidth
          maxWidth='sm'
          onClose={() => setExportDialogOpen(false)}
          open={exportDialogOpen}
        >
          <DialogTitle>Select Categories to Export</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }} variant='body2'>
              Choose which categories to include in the General Summary export:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {summaryData[0]?.categories?.map((category) => (
                <FormControlLabel
                  key={category.name}
                  control={
                    <Checkbox
                      checked={selectedCategories.includes(category.name)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCategories([
                            ...selectedCategories,
                            category.name,
                          ]);
                        } else {
                          setSelectedCategories(
                            selectedCategories.filter(
                              (cat) => cat !== category.name,
                            ),
                          );
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {category.name === 'Delivery Times' && '⏱️ '}
                      {category.name === 'DCPO' && '💰 '}
                      {category.name === 'Driver Supply' && '🚗 '}
                      {category.name === 'Driver Pay & Engagement' && '🧲 '}
                      {category.name === 'Offer Summary' && '📋 '}
                      {category.name}
                      <Chip
                        label={`${category.metrics.length} metrics`}
                        size='small'
                        sx={{ ml: 1 }}
                        variant='outlined'
                      />
                    </Box>
                  }
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
            <Button
              ref={exportButtonRef}
              disabled={selectedCategories.length === 0 || isExporting}
              onClick={exportGeneralSummaryWithSelection}
              startIcon={isExporting ? <CircularProgress size={16} /> : null}
              variant='contained'
            >
              {isExporting
                ? 'Exporting...'
                : `Export (${selectedCategories.length} selected)`}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}
